import clsx from 'clsx'
import {HTMLProps} from 'react'
import {useInView} from 'react-intersection-observer/useInView'
import Paragraph from '../../atoms/paragraph'

export interface TeamMemberCardProps
  extends Omit<HTMLProps<HTMLDivElement>, 'role'> {
  /**
   * Name of the person.
   */
  name: string
  /**
   * Role of the person.
   */
  role: string
  /**
   * Density of team member card items.
   *
   * @default 'default'
   */
  density?: 'default' | 'dense'
  /**
   * Determines whether or not default animation should be disabled.
   *
   * @default false
   */
  disableDefaultAnimation?: boolean
}

export function TeamMemberCard({
  className,
  children: avatar,
  name,
  role,
  density = 'default',
  disableDefaultAnimation,
  ...props
}: TeamMemberCardProps) {
  const {ref, inView} = useInView({
    triggerOnce: true,
    skip: disableDefaultAnimation,
  })

  return (
    <div
      className={clsx(
        'grid items-center grid-flow-row justify-center justify-items-center',
        density === 'dense' ? 'gap-2' : 'gap-4',
        !disableDefaultAnimation
          ? inView
            ? 'motion-safe:animate-fade-in-slide-up motion-reduce:opacity-100'
            : 'opacity-0'
          : 'opacity-100',
        className,
      )}
      ref={ref}
      {...props}
    >
      {avatar}

      <div className={clsx('grid grid-flow-row gap-1 max-w-none')}>
        <span className="text-center leading-normal text-typography font-semibold text-lg">
          {name}
        </span>

        <Paragraph className="text-center text-typography-secondary">
          {role}
        </Paragraph>
      </div>
    </div>
  )
}

export default TeamMemberCard
